import { Box, Button, Grid, Tab, Tabs } from '@mui/material';
import MessagesSnackbar from '../../components/shared/MessagesSnackbar';
import BulkUserManagementBox from './components/BulkUserManagementBox';
import ManageUsersBox from './components/ManageUsersBox';
import useNswagClient from '../../hooks/api/useNswagClient';
import { useContext, useEffect, useState, useRef } from 'react';
import { SuccessResponse_1OfUserListResponse, User, BulkUserActionRequest, Persona } from '../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import useLogError from '../../hooks/useLogError';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import BulkUserReviewTable from './components/BulkUserReviewTable';
import ConfirmationModal from './components/ConfirmationModal';
import type { UserAdjustmentDetails } from './types/UserAdjustmentDetails';
import LoadingWheel from '../../components/ui/LoadingWheel';

const UserManagement = () => {
  const { getUsers, getUsersByFranchise, downloadBulkUserTemplate, bulkUserActions, getPersonas } = useNswagClient();
  const { t } = useTranslation('manageUsers');
  const [showTemplateError, setShowTemplateError] = useState(false);
  const [templateErrorMsg, setTemplateErrorMsg] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tab, setTab] = useState(0);
  const { logError } = useLogError();
  const [reviewUsers, setReviewUsers] = useState<UserAdjustmentDetails[] | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const { isAdmin, selectedFranchise } = useContext(UserContext);
  const [personaData, setPersonaData] = useState<Persona[]>([]);
  const [disableBulkSave, setDisableBulkSave] = useState(false);
  const [downloadTemplateLoading, setDownloadTemplateLoading] = useState(false);

  const newUserNegativeId = useRef(-1);

  useEffect(() => {
    if (isAdmin() || selectedFranchise?.franchiseName) {
      loadData();
    }
  }, [isAdmin, selectedFranchise]);

  useEffect(() => {
    getPersonas().then(res => {
      if (res?.data) setPersonaData(res.data);
    });
  }, []);

  const loadData = () => {
    setIsLoading(true);
    const fetchUsers = isAdmin()
      ? getUsers()
      : getUsersByFranchise(selectedFranchise?.franchiseName);

    fetchUsers
      .then((result: SuccessResponse_1OfUserListResponse) => {
        if (result?.data) {
          setUsers(result.data?.users ?? []);
        }
      })
      .catch((error) => {
        logError(error);
      })
      .finally(() => setIsLoading(false));
  };

  // Helper: Parse XLSX and extract only new, deleted, or changed users
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const parseAndExtractChangedUsers = async (file: File): Promise<UserAdjustmentDetails[]> => {
    const XLSX = await import('xlsx');
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target!.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const parsed: any[] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

          const requiredHeaders = ['email', 'franchise', 'role'];
          const headers = Object.keys(parsed[0] || {});
          const hasAllHeaders = requiredHeaders.every(h => headers.some(col => col.toLowerCase().includes(h)));
          if (!hasAllHeaders) {
            setShowTemplateError(true);
            setTemplateErrorMsg(t('bulkUserTemplateError'));
            reject(new Error('Invalid template'));
            return;
          }

          // Helper to normalize and compare arrays
          const normalizeArray = (arr: any) => {
            if (arr == null) return [];
            if (Array.isArray(arr)) {
              return arr.flatMap((s: any) =>
                typeof s === 'string'
                  ? s.split(',').map((x: string) => x.trim()).filter(Boolean)
                  : [String(s ?? '').trim()],
              ).filter(Boolean);
            }
            if (typeof arr === 'string') {
              return arr.split(',').map((s: string) => s.trim()).filter(Boolean);
            }
            return [String(arr).trim()].filter(Boolean);
          };

          // Helper to parse booleans from Excel (TRUE/FALSE, true/false, 1/0, etc)
          const parseBool = (val: any) => {
            if (typeof val === 'boolean') return val;
            if (typeof val === 'number') return val === 1;
            if (typeof val === 'string') {
              const v = val.trim().toLowerCase();
              return v === 'true' || v === '1' || v === 'yes';
            }
            return false;
          };

          const changedUsers: UserAdjustmentDetails[] = [];

          parsed.forEach((row: any) => {
            const email = (row.email || row.Email || '').toLowerCase();
            if (!email) return; 

            const dbUser = users.find(u => u.emailAddress?.toLowerCase() === email);
            const franchise = row.franchise || row.Franchise || '';
            const role = row.role || row.Role || '';

            const passwordReset = parseBool(row['Password Reset'] ?? row['password reset'] ?? row.passwordReset);
            const deleteUser = parseBool(row['Delete User'] ?? row['delete user'] ?? row.deleteUser);
            const allStores = parseBool(row['All Stores'] ?? row['all stores'] ?? row.allStores);
            const storesCell = row.store ?? row.Store ?? row.STORE ?? row.stores ?? row.Stores ?? row.STORES ?? ''; 
            // New user: always add
            if (!dbUser) {
              changedUsers.push({
                email: row.email || row.Email || '',
                passwordReset,
                deleteUser,
                franchise: franchise,
                role,
                stores: allStores ? [] : normalizeArray(storesCell),
                allStores,
                id: newUserNegativeId.current--, 
              });
              return;
            }

            // Compare fields for existing user
            const roleChanged = (role || '') !== (dbUser.persona?.description || '');
            let storesChanged = false;
            const uploadedStores = normalizeArray(storesCell).map(s => s.trim().toLowerCase()).filter(Boolean);
            const dbStores = (dbUser && dbUser.stores ? dbUser.stores : []).map(s => (typeof s === 'string' ? s : s?.storeName || s?.storeNumber || '').trim().toLowerCase()).filter(Boolean);
            if (uploadedStores.length > 0) {
              const uploadedSet = new Set(uploadedStores);
              const dbSet = new Set(dbStores);
              storesChanged = uploadedStores.length !== dbStores.length || uploadedStores.some(s => !dbSet.has(s)) || dbStores.some(s => !uploadedSet.has(s));
            } else {
              storesChanged = false;
            }
            const franchiseChanged = (franchise || '') !== (dbUser.franchiseName || '');
            const allStoresChanged = typeof allStores !== 'undefined' ? allStores !== !!dbUser.allStores : false;
          
            if (
              email && 
              (roleChanged ||
              storesChanged ||
              franchiseChanged ||
              allStoresChanged ||
              passwordReset === true ||
              deleteUser === true)
            ) {
              changedUsers.push({
                email: row.email || row.Email || '',
                passwordReset,
                deleteUser,
                franchise: franchise || dbUser.franchiseName || '',
                role: role || dbUser.persona?.description || '',
                stores: uploadedStores.length > 0
                  ? uploadedStores
                  : (dbUser.stores || []).map(s => typeof s === 'string' ? s : s?.storeNumber || s?.storeName || ''),
                allStores: typeof allStores !== 'undefined' ? allStores : !!dbUser.allStores,
                id: typeof dbUser.id === 'number' ? dbUser.id : newUserNegativeId.current--, 
              });
            }
          });

          resolve(changedUsers);
        } catch (err) {
          setShowTemplateError(true);
          setTemplateErrorMsg(t('bulkUserTemplateError'));
          reject(err);
        }
      };
      reader.onerror = () => {
        setShowTemplateError(true);
        setTemplateErrorMsg(t('bulkUserTemplateError'));
        reject();
      };
      reader.readAsArrayBuffer(file);
    });
  };
  /* eslint-enable @typescript-eslint/no-explicit-any */

  const mapToBulkUserActionRequest = (user: UserAdjustmentDetails): BulkUserActionRequest => {
    let personaId: number | undefined = undefined;
    if (user.role && user.franchise && personaData) {
      const persona = personaData.find(
        (p: Persona) => p.description === user.role && p.franchiseName === user.franchise,
      );
      if (persona) personaId = persona.id;
    }
    return {
      id: user.id,
      email: user.email,
      franchiseName: user.franchise,
      personaId,
      stores: user.stores,
      allStores: user.allStores,
      passwordReset: user.passwordReset,
      deleteUser: user.deleteUser,
    };
  };

  const hasBulkUserEmailError = (users: UserAdjustmentDetails[]) => {
    if (!users) return false;
    return users.some((user, idx, arr) => {
      const email = user.email?.trim() || '';
      if (!email) return true;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return true;
      return arr.findIndex(u => (u.email?.trim() || '').toLowerCase() === email.toLowerCase()) !== idx;
    });
  };

  return (
    <Box p={10}>
      <Grid container>
        <Grid item
          xs={12}
          sx={{ textAlign: 'left' }}>
          <PageTitle>{t('title')}</PageTitle>
        </Grid>
        <Grid item
          xs={12}
          mt={3}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Tabs
              value={tab}
              onChange={(_e, v) => setTab(v)}
              aria-label="user management tabs"
              sx={{ flex: 1, borderBottom: 'none' }}
            >
              <Tab label={t('userManagementTab')} />
              <Tab label={t('bulkUserManagementTab')} />
            </Tabs>
            {tab === 0 && (
              <Button
                variant="primary"
                size="lg"
                sx={{ ml: 2, minWidth: 180 }}
                onClick={() => {
                  const event = new CustomEvent('openCreateUserModal');
                  window.dispatchEvent(event);
                }}
              >
                {t('createNew')}
              </Button>
            )}
            {tab === 1 && reviewUsers && (
              <>
                <Button
                  variant="secondary"
                  size="lg"
                  sx={{ ml: 2, minWidth: 120 }}
                  onClick={() => setReviewUsers(null)}
                  disabled={disableBulkSave}
                >
                  {t('cancel')}
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  sx={{ ml: 2, minWidth: 120 }}
                  onClick={() => {
                    setDisableBulkSave(true);
                    setShowConfirm(true);
                  }}
                  disabled={hasBulkUserEmailError(reviewUsers) || disableBulkSave}
                >
                  {t('save')}
                </Button>
              </>
            )}
          </Box>
          {tab === 0 && (
            <ManageUsersBox
              users={users}
              loadData={loadData}
              isLoading={isLoading}
            />
          )}
          {tab === 1 && !reviewUsers && (
            isLoading ? (
              <LoadingWheel />
            ) : (
              <BulkUserManagementBox
                onDownloadTemplate={async () => {
                  setDownloadTemplateLoading(true);
                  try {
                    const response = await downloadBulkUserTemplate(selectedFranchise?.franchiseName);
                    const blob = response.data;
                    const fileName = response.fileName || 'UserImportTemplate.xlsx';
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    logError(error);
                  } finally {
                    setDownloadTemplateLoading(false);
                  }
                }}
                onUploadTemplate={async e => {
                  const files = e.target.files;
                  if (!files || !files[0]) {
                    setShowTemplateError(true);
                    setTemplateErrorMsg(t('noFileSelected'));
                    return;
                  }
                  const file = files[0];
                  if (
                    file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' &&
                    !file.name.endsWith('.xlsx')
                  ) {
                    setShowTemplateError(true);
                    setTemplateErrorMsg(t('invalidXlsxFile'));
                    return;
                  }
                  try {
                    const changedUsers = await parseAndExtractChangedUsers(file);
                    setReviewUsers(changedUsers as UserAdjustmentDetails[]);
                  } catch (err) {logError(err);}
                }}
                downloadTemplateLoading={downloadTemplateLoading}
              />
            )
          )}
          {tab === 1 && reviewUsers && (
            isLoading || users.length === 0 ? (
              <LoadingWheel />
            ) : (
              <BulkUserReviewTable
                users={reviewUsers}
                onChange={(users: UserAdjustmentDetails[]) => setReviewUsers(users)}
                personaData={personaData}
              />
            )
          )}
          <ConfirmationModal
            open={showConfirm}
            onConfirm={async () => {
              if (!reviewUsers) {
                setDisableBulkSave(false);
                return;
              }
              setShowConfirm(false);
              try {
                const body = reviewUsers.map(mapToBulkUserActionRequest);
                const response = await bulkUserActions(body);
                interface BulkUserActionResponse {
                  success: boolean;
                  email: string;
                  error?: string;
                }
                const failed: BulkUserActionResponse[] = (response as BulkUserActionResponse[]).filter((r: BulkUserActionResponse) => !r.success);
                if (failed.length === 0) {
                  setShowTemplateError(false);
                  setTemplateErrorMsg(null);
                  setShowSuccess(true);
                  setSuccessMsg(t('bulkUserActionSuccess'));
                  setReviewUsers(null);
                  loadData();
                } else {
                  setShowTemplateError(true);
                  setTemplateErrorMsg(`${failed.length} user(s) failed: ` + failed.map(f => `${f.email}: ${f.error}`).join('; '));
                }
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } catch (err: any) {
                setShowTemplateError(true);
                setTemplateErrorMsg('Bulk user action failed: ' + (err?.message || 'Unknown error'));
              } finally {
                setDisableBulkSave(false);
              }
            }}
            onCancel={() => {
              setShowConfirm(false);
              setDisableBulkSave(false);
            }}
          />
          <MessagesSnackbar
            open={showTemplateError}
            onClose={() => setShowTemplateError(false)}
            message={templateErrorMsg || t('bulkUserTemplateError')}
            severity="error"
            duration={null}
          />
          <MessagesSnackbar
            open={showSuccess}
            onClose={() => setShowSuccess(false)}
            message={successMsg || t('bulkUserActionSuccess')}
            severity="success"
            duration={null}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserManagement;